import React, { useCallback, useEffect, useState } from "react";
import { Button, Col, DatePicker, Form, Input, Row, Select } from "antd";
import moment from "moment";
import DepartmentsDataStore, {DeclarantInvoiceType,} from "../../../stores/pages/DepartmentsDataStore";
import { Client } from "../../../models/ByClients/Client";
import { ExecutableModalProps } from "../../../models/Modals";
import ExecutableModal from "../../executableModal/ExecutableModal";
import ConditionalFormSection from "../../formItems/ConditionalFormSection";
import {InvoiceType, invoiceTypes,} from "../../../models/DepartmentData/InvoiceType";
import {GenerateInvoiceFormValues} from "./types";
import {
  GenerateInvoiceFormItem,
  invoiceCustomTitleFieldMap,
  invoiceDiscountFieldsMap,
  invoiceExtraFieldsMap,
  invoiceTitleFieldsMap,
  specificationInvoiceSelectOptions
} from "./lib";
import {Nullable} from "../../../models/Common";

const { TextArea } = Input;

const GUARANTOR_JOB_TITLE_OPTIONS = [
  { value: "Бухгалтер", label: "Бухгалтер" },
  { value: "Главный бухгалтер", label: "Главный бухгалтер" },
];

const GUARANTOR_NAME_OPTIONS = [
  { value: "Добренко А.Л.", label: "Добренко А.Л." },
  { value: "Сухоцкая И.В.", label: "Сухоцкая И.В." },
  { value: "Миклашевич А.Ю.", label: "Миклашевич А.Ю." },
];

type Props = ExecutableModalProps<{
  store: DepartmentsDataStore;
  client: Client | null | undefined;
}>;

interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

const GenerateInvoiceModal: React.FC<Props> = ({ handler, store, client }) => {
  const [form] = Form.useForm<GenerateInvoiceFormValues>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [invoiceOptions, setInvoiceOptions] = useState<SelectOption[] | null>(
    null
  );

  const fetchInvoiceTypes = useCallback(async () => {
    const types: DeclarantInvoiceType[] | null = await store.getInvoiceTypes();
    setInvoiceOptions(
      types
        ? types.map(
            (type: DeclarantInvoiceType): SelectOption => ({
              value: type.key,
              label: type.label,
            })
          )
        : []
    );
  }, [store]);

  const devRestrictOptions = useCallback(
    (options: SelectOption[]): SelectOption[] =>
      options.map(
        (option: SelectOption): SelectOption => ({
          ...option,
          disabled: !invoiceTypes.includes(option.value as InvoiceType),
        })
      ),
    []
  );

  const onShow = useCallback(() => {
    form.resetFields(["discountType", "discountValue"]);
    const firstAvailableInvoice: number = devRestrictOptions(invoiceOptions || []).findIndex((opt: SelectOption) => {
      return !opt.disabled;
    });
    const invoiceValue: InvoiceType = (firstAvailableInvoice === -1 ? "" : invoiceOptions![firstAvailableInvoice].value) as InvoiceType;
    form.setFieldsValue({
      paymentDetails: client?.payment_details || "",
      invoicePrefix: client?.invoice_prefix || "",
      invoiceDate: moment(Date.now()),
      indexNumber: "",
      invoiceType: invoiceValue,
      contractReference: client?.contract_reference || "",
      guarantorJobTitle: "Бухгалтер",
      guarantorName: "Миклашевич А.Ю.",
    });
  }, [client, form, invoiceOptions]);

  useEffect(() => {
    if (!invoiceOptions) {
      fetchInvoiceTypes();
    }
  }, [invoiceOptions, fetchInvoiceTypes]);

  const handleGenerate = useCallback(async () => {
    let values: GenerateInvoiceFormValues;
    try {
      values = await form.validateFields();
    } catch {
      // Ant Design displays form validation errors inline.
      return;
    }

    setIsLoading(true);
    try {
      const title: Nullable<string> = values.prefilledTitle
        ? (values.prefilledTitle === 'other'
          ? values.title
          : specificationInvoiceSelectOptions.getLabel(values.prefilledTitle)
        ) : null;
      const generated = await store.generateInvoice({
        ...values,
        title,
        invoiceDate: values.invoiceDate.format("DD.MM.YYYY"),
      });
      if (generated) {
        store.refresh();
        handler.executeClose();
      }
    } finally {
      setIsLoading(false);
    }
  }, [form, handler, store]);

  const handleFormValuesChange = useCallback((changedValues: Partial<GenerateInvoiceFormValues>): void => {
    if ("invoiceType" in changedValues) {
      form.resetFields(["discountType", "discountValue"]);
    } else if ("discountType" in changedValues) {
      form.resetFields(["discountValue"]);
    }
  }, [form]);

  return (
    <ExecutableModal
      handler={handler}
      onShow={onShow}
      title="Generate Invoice"
      footer={[
        <Button key="cancel" onClick={handler.executeClose}>
          Cancel
        </Button>,
        <Button
          key="generate"
          type="primary"
          loading={isLoading}
          onClick={handleGenerate}
        >
          Generate PDF
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" onValuesChange={handleFormValuesChange}>
        <GenerateInvoiceFormItem label="Invoice Type" name="invoiceType">
          <Select
            options={devRestrictOptions(invoiceOptions || [])}
            loading={invoiceOptions === null}
          />
        </GenerateInvoiceFormItem>

        <ConditionalFormSection field="invoiceType" map={invoiceTitleFieldsMap} />
        <ConditionalFormSection field="prefilledTitle" map={invoiceCustomTitleFieldMap} />
        <ConditionalFormSection field="invoiceType" map={invoiceDiscountFieldsMap} />

        <GenerateInvoiceFormItem name="invoiceDate" label="Invoice Date">
          <DatePicker style={{ width: "100%" }} />
        </GenerateInvoiceFormItem>
        <GenerateInvoiceFormItem
          label="Client Payment Details"
          name="paymentDetails"
        >
          <TextArea rows={4} placeholder="Enter client payment details" />
        </GenerateInvoiceFormItem>
        <GenerateInvoiceFormItem
          label="Invoice Number Prefix"
          name="invoicePrefix"
        >
          <Input placeholder="e.g. CLI" />
        </GenerateInvoiceFormItem>
        <GenerateInvoiceFormItem
          label="Invoice Count Number"
          name="indexNumber"
        >
          <Input placeholder="e.g. 044" />
        </GenerateInvoiceFormItem>
        <ConditionalFormSection
          field="invoiceType"
          map={invoiceExtraFieldsMap}
        />
        <GenerateInvoiceFormItem name="reisas" label="REISAS">
          <Input placeholder="Enter REISAS" />
        </GenerateInvoiceFormItem>
        <Form.Item label="Guarantor Person">
          <Row gutter={8}>
            <Col span={12}>
              <Form.Item name="guarantorJobTitle" noStyle>
                <Select
                  options={GUARANTOR_JOB_TITLE_OPTIONS}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="guarantorName" noStyle>
                <Select
                  options={GUARANTOR_NAME_OPTIONS}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form.Item>
      </Form>
    </ExecutableModal>
  );
};

export default GenerateInvoiceModal;
